import { z } from 'zod'
import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'
import type { StatusTone } from '../../ui/StatusBadge/StatusBadge'

const state = z.enum(['INITIALIZING', 'SYNCING', 'READY', 'DEGRADED_READ'])
export const readSideStatusSchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantUid: z.string().min(1),
    state: state.nullable(),
    resourcesReady: z.boolean(),
    todayReady: z.boolean(),
    patientDirectory: z
      .object({
        state,
        initialBackfillComplete: z.boolean(),
        pagesCompleted: z.number().int().nonnegative(),
        recordsObserved: z.number().int().nonnegative(),
        recordsProjected: z.number().int().nonnegative(),
        updatedAtMs: z.number().int().nonnegative().nullable(),
      })
      .strict(),
  })
  .strict()
export type ReadSideStatus = z.infer<typeof readSideStatusSchema>
export type ReadSideRepository = {
  getStatus: (tenantId: string) => Promise<ReadSideStatus>
}
export function createReadSideRepository({
  getUser,
  baseUrl = getEnvironment().onboardingApiBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): ReadSideRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  return {
    async getStatus(tenantId) {
      const result = await client.request('/v1/settings/read-side-status', {
        method: 'POST',
        body: { tenantUid: tenantId },
        responseSchema: readSideStatusSchema,
      })
      if (result.tenantUid !== tenantId)
        throw new Error('Clinic read status is out of scope')
      return result
    },
  }
}
export function readSidePresentation(
  status: ReadSideStatus['state']
): { label: string; tone: StatusTone } | null {
  switch (status) {
    case 'INITIALIZING':
      return { label: 'Initializing', tone: 'warning' }
    case 'SYNCING':
      return { label: 'Syncing', tone: 'warning' }
    case 'READY':
      return { label: 'Live', tone: 'success' }
    case 'DEGRADED_READ':
      return { label: 'Read sync delayed', tone: 'warning' }
    default:
      return null
  }
}
export function patientDirectoryMessage(status: ReadSideStatus): string | null {
  if (
    status.state === null ||
    status.patientDirectory.initialBackfillComplete
  )
    return null
  const count = status.patientDirectory.recordsProjected
  return status.patientDirectory.state === 'DEGRADED_READ' ||
    (status.state === 'DEGRADED_READ' && !status.resourcesReady)
    ? `Patient directory sync is delayed. ${count} records processed; available patients remain accessible.`
    : `Patient directory is syncing. ${count} records processed. You can use available patients while the rest load.`
}
